import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import {
  sideBar,
  sideBar2,
  SideBar2Const,
  parentSideBar,
  parentSideBar2,
  studentNavV2Learn,
  studentNavV2Manage,
  studentNavV2Top,
} from '../../core/constants/sidebar.const';
import { SideBar } from '../../models/sideBar.model';
import { assets } from '../../core/constants/assets.const';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { dashboardNotification, UserResponse } from '../../models/auth.model';
import { UserRole } from '../../core/constants/keys.constant';
import { SocketService } from '../../services/socket.service';

@Component({
  selector: 'mindbase-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit, OnDestroy {

  private destroy: Subject<boolean> = new Subject<boolean>();
  protected subscriptionSubject$!: Subscription;
  protected assetsConst = assets;
  public sideBar = sideBar;
  public sideBar2 = sideBar2;
  public parentSideBar = parentSideBar;
  public parentSideBar2 = parentSideBar2;
  /** Version 2–style grouped nav (see `AppSidebar.tsx`). */
  protected readonly studentNavV2Top = studentNavV2Top;
  protected readonly studentNavV2Learn = studentNavV2Learn;
  protected readonly studentNavV2Manage = studentNavV2Manage;
  protected learnGroupOpen = false;
  protected manageGroupOpen = false;
  protected profile: UserResponse | null = null;
  protected UserRole = UserRole;
  protected webLogo: any;
  protected SideBar2Const = SideBar2Const;
  public innerWidth = 0;
  public sidenavWidth = 5;
  public supportNotificationCount : number | undefined = 0;
  public chatNotificationCount: number | undefined = 0;
  public notificationCount: number | undefined = 0;
  /** Gates the "Universities Wishlist" / "Scholarship wishlist" nav items (admin: Permissions → Wishlist). */
  protected canManageUniversityWishlist = true;
  protected canManageScholarshipWishlist = true;
  /** Gates the "AI Counselor" nav item (admin: Permissions → My Counselor → AI Counselor). */
  protected canAccessAiCounselor = true;
  /** Gates the "Study with AI Progress" nav item (admin: Permissions → My Counselor → Study with AI). */
  protected canAccessStudyWithAi = true;
  /** Gates the "Courses V2" nav item (admin: Permissions → Pathway Features → My Courses). */
  // protected canAccessMyCourses = true;
  /** Gates the "AI Gamification" nav item (admin: Permissions → Pathway Features → AI Achievements). */
  protected canAccessAiAchievements = true;
  /** Gates the Achievements nav item's "Pathways" tab (admin: Permissions → Pathway Features → Normal Achievements). */
  protected canAccessAchievements = true;
  /** Gates the "Counselling Hub" nav item (admin: Permissions → Pathway Features → Counselling Hub). */
  protected canAccessCounsellingHub = true;
  /** Gates the "Dashboard" nav item (admin: Permissions → Pathway Features → Dashboard Metrics). */
  protected canAccessDashboardMetrics = true;
  constructor(
    public _authService: AuthService,
    protected router: Router,
    private _socketService: SocketService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscriptionSubject$ = this._authService.profileDetailsSubject
      .pipe(takeUntil(this.destroy))
      .subscribe((res: UserResponse | null) => {
        if (res !== null) {
          this.profile = res;
          this.applyWishlistNavPermissions(res);
          this.applyMyCounselorNavPermissions(res);
          this.applyPathwayFeaturesNavPermissions(res);
          this.syncStudentGroupExpansion();
        }
      });

    this._authService.permissionUpdate
      .pipe(takeUntil(this.destroy))
      .subscribe((res: any) => {
        if (res !== null) {
          this.canManageUniversityWishlist =
            res.student?.wishlist?.canManageUniversityWishlist ?? false;
          this.canManageScholarshipWishlist =
            res.student?.wishlist?.canManageScholarshipWishlist ?? false;
          this.canAccessAiCounselor =
            res.student?.myCounselor?.canAccessAiCounselor ?? false;
          this.canAccessStudyWithAi =
            res.student?.myCounselor?.canAccessStudyWithAi ?? false;
          this.canAccessAiAchievements =
            res.student?.pathwayFeatures?.canAccessAiAchievements ?? false;
          this.canAccessAchievements =
            res.student?.pathwayFeatures?.canAccessAchievements ?? false;
          this.canAccessCounsellingHub =
            res.student?.pathwayFeatures?.canAccessCounsellingHub ?? false;
          this.canAccessDashboardMetrics =
            res.student?.pathwayFeatures?.canAccessDashboardMetrics ?? false;
        }
      });
    this.webLogo = this.getWebSettings()?.webLogo || this.assetsConst.logo;
    setTimeout(() => {
      const updatedLogo = this.getWebSettings()?.webLogo;
      if (updatedLogo) {
        this.webLogo = updatedLogo;
        this.cdr.detectChanges();
      }
    }, 500); 
    this.getNotificationCount();
    this.getSupportCount();
    this.getChatCount();
    this.chatCountUpdated();
    this.noti_received();
    this.supportCountUpdated();
    this.clearNotifications();

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy)
      )
      .subscribe(() => this.syncStudentGroupExpansion());
    this.syncStudentGroupExpansion();
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }

  /** STUDENT/PARENT only; other roles keep the nav items unaffected. */
  private applyWishlistNavPermissions(profile: UserResponse): void {
    if (profile.role === UserRole.STUDENT) {
      this.canManageUniversityWishlist =
        profile.userPermissions?.student?.wishlist?.canManageUniversityWishlist ?? false;
      this.canManageScholarshipWishlist =
        profile.userPermissions?.student?.wishlist?.canManageScholarshipWishlist ?? false;
    } else if (profile.role === UserRole.PARENT) {
      this.canManageUniversityWishlist =
        profile.parentPermissions?.student?.wishlist?.canManageUniversityWishlist ?? false;
      this.canManageScholarshipWishlist =
        profile.parentPermissions?.student?.wishlist?.canManageScholarshipWishlist ?? false;
    }
  }

  /** STUDENT/PARENT only; other roles keep the nav item unaffected. */
  private applyMyCounselorNavPermissions(profile: UserResponse): void {
    if (profile.role === UserRole.STUDENT) {
      this.canAccessAiCounselor =
        profile.userPermissions?.student?.myCounselor?.canAccessAiCounselor ?? false;
      this.canAccessStudyWithAi =
        profile.userPermissions?.student?.myCounselor?.canAccessStudyWithAi ?? false;
    } else if (profile.role === UserRole.PARENT) {
      this.canAccessAiCounselor =
        profile.parentPermissions?.student?.myCounselor?.canAccessAiCounselor ?? false;
      this.canAccessStudyWithAi =
        profile.parentPermissions?.student?.myCounselor?.canAccessStudyWithAi ?? false;
    }
  }

  /** STUDENT/PARENT only; other roles keep the nav item unaffected. */
  private applyPathwayFeaturesNavPermissions(profile: UserResponse): void {
    if (profile.role === UserRole.STUDENT) {
      this.canAccessAiAchievements =
        profile.userPermissions?.student?.pathwayFeatures?.canAccessAiAchievements ?? false;
      this.canAccessAchievements =
        profile.userPermissions?.student?.pathwayFeatures?.canAccessAchievements ?? false;
      this.canAccessCounsellingHub =
        profile.userPermissions?.student?.pathwayFeatures?.canAccessCounsellingHub ?? false;
      this.canAccessDashboardMetrics =
        profile.userPermissions?.student?.pathwayFeatures?.canAccessDashboardMetrics ?? false;
    } else if (profile.role === UserRole.PARENT) {
      this.canAccessAiAchievements =
        profile.parentPermissions?.student?.pathwayFeatures?.canAccessAiAchievements ?? false;
      this.canAccessAchievements =
        profile.parentPermissions?.student?.pathwayFeatures?.canAccessAchievements ?? false;
      this.canAccessCounsellingHub =
        profile.parentPermissions?.student?.pathwayFeatures?.canAccessCounsellingHub ?? false;
      this.canAccessDashboardMetrics =
        profile.parentPermissions?.student?.pathwayFeatures?.canAccessDashboardMetrics ?? false;
    }
  }

  /** Hides nav items whose feature permission is off (admin: Permissions → Wishlist / My Counselor / Pathway Features). */
  protected isSidebarItemHidden(item: SideBar): boolean {
    // Counselling is a student-facing hub (browse and book counsellors for yourself).
    // Parents reach counselling through their student's profile instead, so the top-level
    // nav entry is hidden for them — the /manage-counselling/* routes they are sent to
    // from that profile (essay, interview, files) still work.
    if (item.path === '/manage-counselling/counsellors' && this.profile?.role === UserRole.PARENT) {
      return true;
    }
    // Both wishlists are student-owned lists. A parent reaches them only from their
    // student's profile screen (which carries `studentId` through), so the top-level nav
    // entries are hidden for them — opened from the nav they would have no student context
    // and the list APIs reject the call.
    if (
      (item.path === '/university-wishlist' ||
        item.path === '/scholarship-wishlist') &&
      this.profile?.role === UserRole.PARENT
    ) {
      return true;
    }
    if (item.path === '/university-wishlist') {
      return !this.canManageUniversityWishlist;
    }
    if (item.path === '/scholarship-wishlist') {
      return !this.canManageScholarshipWishlist;
    }
    if (item.path === '/ai-counselor') {
      return !this.canAccessAiCounselor;
    }
    if (item.path === '/study-with-ai-progress') {
      return !this.canAccessStudyWithAi;
    }
    // Achievements hosts both the AI and Pathways ("Normal") tracks as tabs; the nav
    // entry stays visible as long as at least one of the two is granted.
    if (item.path === '/dashboard/achievements') {
      return !this.canAccessAiAchievements && !this.canAccessAchievements;
    }
    if (item.path === '/manage-counselling/hub') {
      return !this.canAccessCounsellingHub;
    }
    if (item.path === '/dashboard/student-dashboard') {
      return !this.canAccessDashboardMetrics;
    }
    return false;
  }

  /** Open Learn / Manage when the active route falls in that group (version 2 behavior). */
  private syncStudentGroupExpansion(): void {
    if (this.profile?.role !== UserRole.STUDENT) {
      return;
    }
    if (studentNavV2Learn.some((i) => this.isRouteActive(i))) {
      this.learnGroupOpen = true;
    }
    if (studentNavV2Manage.some((i) => this.isRouteActive(i))) {
      this.manageGroupOpen = true;
    }
  }

  getWebSettings(): any {
    const val = localStorage.getItem('webSettings');
    if (!val || val === 'undefined') return {};
    try {
      return JSON.parse(val);
    } catch {
      return {};
    }
  }

  increase(): void {
    this.sidenavWidth = 15;
  }
  decrease(): void {
    this.sidenavWidth = 4;
  }
  @HostListener('window', ['$event'])
  toggleSidenav(_event?: Event): void {
    this.innerWidth = window.innerWidth;
    if (this.innerWidth <= 1199.98) {
      this._authService.toggleSidebarView();
    }
  }

  protected toggleLearnGroup(): void {
    this.learnGroupOpen = !this.learnGroupOpen;
  }

  protected toggleManageGroup(): void {
    this.manageGroupOpen = !this.manageGroupOpen;
  }

  /** Resolved router link — legacy Dashboard / Courses items use their `path` as-is for students. */
  navPath(item: SideBar): string {
    return item.path ?? '/';
  }

  get logoHomePath(): string {
    return this.profile?.role === UserRole.STUDENT ? '/dashboard/dashboard-v2' : '/dashboard';
  }

  isRouteActive(item: SideBar): boolean {
    const path = item.path ?? '';
    const currentUrl = this.router.url.split('?')[0]; // Remove query parameters from the URL

    // Students' "Home" is the version 2 dashboard; `/dashboard` (still used by
    // post-login redirects, which the route guard forwards here) is the same nav item.
    if (path === '/dashboard/dashboard-v2' && this.profile?.role === UserRole.STUDENT) {
      return (
        currentUrl === '/dashboard' ||
        currentUrl === '/dashboard/' ||
        currentUrl === '/dashboard/dashboard-v2' ||
        currentUrl.startsWith('/dashboard/explore-pathways') ||
        currentUrl.startsWith('/dashboard/progress') ||
        currentUrl.startsWith('/dashboard/career-assessment')
      );
    }

    if (path === '/manage-courses' && this.profile?.role === UserRole.STUDENT) {
      return currentUrl.startsWith('/manage-courses');
    }

    if (path === '/dashboard/courses-v2' && this.profile?.role === UserRole.STUDENT) {
      return (
        currentUrl.startsWith('/dashboard/courses-v2') || currentUrl.startsWith('/dashboard/course-learning')
      );
    }

    if (this.profile?.role === UserRole.PARENT && currentUrl === '/manage-courses' && path === '/manage-courses') {
    // Check if query params exist (studentId, etc.)
      const fullUrl = this.router.url;
      if (!fullUrl.includes('?') || !fullUrl.includes('studentId')) {
        this.router.navigate(['/manage-courses/students']);
      }
      return false;
    }
  
    // Exclude specific route from being marked as active
    if (path === '/manage-tutoring/tutor-package') {
      return false;
    }

    if (currentUrl === '/manage-counselling/all-counsellors' && path === '/manage-counselling/counsellors') {
      return true;
    }

    // Check if the current URL starts with the given path
    return currentUrl.startsWith(path);
  }

  protected clearNotifications(): void {
    this._authService.chatClicked.subscribe((res: any) => {
      if (res) {
        this.chatNotificationCount = 0;
      }
    });
    this._authService.notificationClicked.subscribe((res: any) => {
      if (res) {
        this.notificationCount = 0;
      }
    });
    this._authService.supportClicked.subscribe((res: any) => {
      if (res) {
        this.getSupportCount();
      }
    });
  }

  protected getNotificationCount(): void {
    this._authService
      .getNotificationCount()
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: dashboardNotification) => {
          this.notificationCount = res.receiverNotificationCount;
        }
      });
  }

  protected getChatCount(): void {
    this._authService
      .getChatCount()
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: dashboardNotification) => {
          this.chatNotificationCount = res.receiverChatCount;
        }
      });
  }

  protected getSupportCount(): void {
    this._authService
      .getSupportCount()
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: dashboardNotification) => {
          this.supportNotificationCount = res.receiverSupportTicketCount.totalUnreadSupportTickets;
        }
      });
  }

  private chatCountUpdated(): void {
    this._socketService.onEvent('chatCountUpdated').subscribe({
      next: (res: any) => {
        this.chatNotificationCount = res.data;
      }
    });
  }

  private supportCountUpdated(): void {
    this._socketService.onEvent('SupportTicketCountUpdated').subscribe({
      next: (res: any) => {
        this.supportNotificationCount = res.data.totalUnreadSupportTickets;
      }
    });
  }

  private noti_received(): void {
    this._socketService.onEvent('noti_received').subscribe({
      next: (res: any) => {
        this.notificationCount = res.data;
      }
    });
  }

}

